import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Sparkles } from "lucide-react";
import { DEMO_LINK } from "@/App";

const HERO_IMG =
  "https://images.pexels.com/photos/12821628/pexels-photo-12821628.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1600";

const lines = ["Never Miss", "Another", "Phone Order."];

const MaskLine = ({ children, delay }) => (
  <span className="block overflow-hidden">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.55, 0.85]);

  return (
    <section ref={ref} id="top" className="relative min-h-screen flex items-end overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0 z-0">
        <img src={HERO_IMG} alt="Chef working in a restaurant kitchen at night" className="w-full h-full object-cover" />
      </motion.div>
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 z-10 bg-ink" />
      <div className="absolute inset-0 z-10 bg-gradient-to-t from-ink via-ink/40 to-transparent" />

      <div className="relative z-20 w-full max-w-7xl mx-auto px-4 md:px-8 pb-16 md:pb-24 pt-32">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="inline-flex items-center gap-2 rounded-full border border-line bg-ink/50 backdrop-blur px-4 py-1.5 mb-8 text-xs font-medium tracking-wide text-sand"
        >
          <Sparkles className="w-3.5 h-3.5 text-terra" strokeWidth={1.5} />
          AI VOICE ORDERING FOR RESTAURANTS · 24/7
        </motion.div>

        <h1 className="font-heading text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold tracking-tighter leading-[0.95] text-cream">
          <MaskLine delay={0.2}>Never Miss</MaskLine>
          <MaskLine delay={0.32}>Another</MaskLine>
          <MaskLine delay={0.44}>
            <span className="text-terra">Phone Order.</span>
          </MaskLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.7 }}
          className="mt-8 max-w-2xl text-base sm:text-lg leading-relaxed text-sand"
        >
          An AI assistant answers your restaurant's calls, takes orders accurately, and drops
          them right into your POS, 24/7.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.85 }}
          className="mt-10"
        >
          <a
            href={DEMO_LINK}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-demo-button"
            className="group inline-flex items-center gap-3 rounded-full bg-terra px-8 py-4 text-base font-semibold text-cream hover:bg-terra-hover transition-[background-color,transform] duration-300 hover:-translate-y-1"
          >
            Book a Free Demo
            <ArrowUpRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.5} />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
